import clsx from 'clsx'
import dayjs from 'dayjs'
import { computed, defineComponent, ref, watch } from 'vue'

import VButton from '@/components/common/button/VButton'
import VIcon from '@/components/common/VIcon'
import { isNil } from '@/utils/validation'

interface Props {
  class?: string
  value?: dayjs.Dayjs | null
  max?: dayjs.Dayjs | null
  min?: dayjs.Dayjs | null
  fullWidth?: boolean
  isbuddhistEra?: boolean
  color?: 'white'
  onChange: (selectedDate: dayjs.Dayjs) => void
}

export default defineComponent<Props>((props) => {
  const MONTH_SHORT_NAMES = Array.from({ length: 12 }, (_, i) =>
    dayjs().month(i).format('MMM')
  )

  const DAYS = Array.from({ length: 7 }, (_, i) =>
    dayjs().day(i).format('dddd').replace('เ', '').charAt(0)
  )

  // #region Props Getters

  const fullWidth = computed((): boolean => props.fullWidth ?? false)
  const isbuddhistEra = computed((): boolean => props.isbuddhistEra ?? false)

  // #endregion

  const month = ref<number>(isNil(props.value) ? (new Date()).getMonth() : props.value.month())
  const year = ref<number>(isNil(props.value) ? (new Date()).getFullYear() : props.value.year())

  // #region Getters

  const displayYear = computed(() => isbuddhistEra.value ? year.value + 543 : year.value)

  const mapNoOfDays = computed(() => {
    const daysInMonth = new Date(year.value, month.value + 1, 0).getDate()
    const noOfDays: number[] = []
    for (let i = 1; i <= daysInMonth; i++) {
      noOfDays.push(i)
    }

    return noOfDays.map((noOfDay, index) => {
      const isLessThanMin = checkLessThanMin(noOfDay)
      const isMoreThanMax = checkMoreThanMax(noOfDay)
      const isSelectedDate = checkSelectedDate(noOfDay)
      const isToday = checkToday(noOfDay)
      const disabled = isLessThanMin || isMoreThanMax

      return (
        <div
          key={`noOfDay-${year.value}-${month.value}-${index}`}
          class='flex aspect-square items-center justify-center whitespace-nowrap'
        >
          <button
            t-data='date-picker-btn'
            class={[
              'input-label flex size-full items-center justify-center rounded-full border transition duration-100 ease-in-out disabled:cursor-not-allowed disabled:text-neutral-400 disabled:hover:bg-transparent',
              isSelectedDate || isToday ? 'border-primary' : 'border-transparent',
              isSelectedDate ? 'bg-primary text-white hover:bg-primary-600' : 'hover:bg-neutral-100'
            ]}
            disabled={disabled}
            onMousedown={(e: Event) => {
              e.preventDefault()

              getDateValue(noOfDay)
            }}
          >{noOfDay}
          </button>
        </div>
      )
    })
  })

  const mapBlankdays = computed(() => {
    const dayOfWeek = new Date(year.value, month.value).getDay()
    const blankdays: number[] = []
    for (let i = 1; i <= dayOfWeek; i++) {
      blankdays.push(i)
    }

    return blankdays.map((_blankday, index) => {
      return (
        <div
          key={`blankday-${year.value}-${month.value}-${index}`}
          class='spect-square'
        />
      )
    })
  })

  const changeMonthBtnColor = computed((): string | undefined => {
    if (props.color === 'white') {
      return clsx('btn-white')
    }

    return undefined
  })

  // #endregion

  // #region Watchers

  watch(() => props.value, () => {
    month.value = isNil(props.value) ? (new Date()).getMonth() : props.value.month()
    year.value = isNil(props.value) ? (new Date()).getFullYear() : props.value.year()
  })

  // #endregion

  // #region Action

  const checkToday = (date: number): boolean => {
    const today = new Date()
    const d = new Date(year.value, month.value, date)

    return today.toDateString() === d.toDateString()
  }

  const checkLessThanMin = (date: number): boolean => {
    if (isNil(props.min)) {
      return false
    }

    const d = new Date(year.value, month.value, date)

    return props.min.unix() > dayjs(d).unix()
  }

  const checkMoreThanMax = (date: number): boolean => {
    if (isNil(props.max)) {
      return false
    }

    const d = new Date(year.value, month.value, date)

    return props.max.unix() < dayjs(d).unix()
  }

  const checkSelectedDate = (date: number): boolean => {
    const d = new Date(year.value, month.value, date)

    return props.value !== null &&
      props.value !== undefined &&
      props.value.toDate().toDateString() === d.toDateString()
  }

  const getDateValue = (date: number): void => {
    const selectedDate = new Date(year.value, month.value, date)
    props.onChange?.(dayjs(selectedDate))
  }

  // #endregion

  return () => (
    <div
      class={clsx(
        'max-h-[929px] min-w-[272px] overflow-hidden rounded bg-white shadow-elevation-1',
        {
          'w-full': fullWidth.value,
          'w-[272px]': !fullWidth.value
        },
        props.class
      )}
    >
      <div
        class={clsx(
          'flex items-center justify-between space-x-1 px-3 py-2',
          {
            'bg-primary': props.color === undefined,
            'border-b border-neutral-300': props.color !== undefined
          }
        )}
      >
        <VButton
          type='button'
          t-data='date-picker-previous-month-btn'
          class='btn-small'
          color={changeMonthBtnColor.value}
          onMousedown={(e: Event) => {
            e.preventDefault()

            month.value--

            if (month.value < 0) {
              month.value = 11
              year.value--
            }
          }}
        >
          <VIcon
            class='text-[20px]'
            icon='material-symbols:keyboard-backspace'
          />
        </VButton>
        <div class={clsx('text-body-1', { 'text-white': props.color === undefined })}>{MONTH_SHORT_NAMES[month.value]} {displayYear.value}</div>
        <VButton
          type='button'
          t-data='date-picker-next-month-btn'
          class='btn-small'
          color={changeMonthBtnColor.value}
          onMousedown={(e: Event) => {
            e.preventDefault()

            month.value++

            if (month.value > 11) {
              month.value = 0
              year.value++
            }
          }}
        >
          <VIcon
            class='rotate-180 text-[20px]'
            icon='material-symbols:keyboard-backspace'
          />
        </VButton>
      </div>
      <div class='px-3 py-4'>
        <div class='grid grid-cols-7 gap-1'>
          {DAYS.map((day, index) => {
            return (
              <div
                key={`day-${index}`}
                class='input-label flex aspect-square items-center justify-center whitespace-nowrap text-primary'
              >
                {day}
              </div>
            )
          })}
        </div>
        <div class='grid grid-cols-7 gap-1'>
          {mapBlankdays.value}
          {mapNoOfDays.value}
        </div>
      </div>
    </div>
  )
}, {
  name: 'DatePicker',
  props: [
    'class',
    'value',
    'min',
    'max',
    'fullWidth',
    'isbuddhistEra',
    'color',
    'onChange'
  ]
})
